/**
 * PATH: packages/kanban-host/src/allocation/buildAllocationSummary.ts
 * FILE_ID: KANBANHOST-ALLOC-SUMMARY-BUILD-001
 * PURPOSE: Build an EmailAllocationSummary from allocate/link outcomes
 *          so inbox hosts can patch the row locally (no list refetch).
 * WHY: Full inbox refetch after allocate resets list scroll; BL-1
 *      already patches other mutations locally — allocate was the gap.
 */

import type { EmailAllocationSummary } from '@quotech/tenant-shared/email/api'
import type {
  AllocateEmailToLaneResult,
  CardSearchResult,
  LinkEmailToCardResult,
} from '@quotech/ui/kanban'

export interface ManualAllocateBoardMeta {
  id: string
  name: string
  swatch?: string | null
  kind?: string | null
  parent_board_id?: string | null
  parent_board_name?: string | null
}

export interface ManualAllocateLaneMeta {
  id: string
  name: string
  kind?: string | null
  color?: string | null
}

/** Pure I→O — summary for a successful create-card allocate. */
export function buildSummaryFromAllocateResult(input: {
  result: AllocateEmailToLaneResult
  board: ManualAllocateBoardMeta
  lane: ManualAllocateLaneMeta
}): EmailAllocationSummary {
  const { result, board, lane } = input
  return {
    allocation_id: result.allocation.id,
    card_id: result.card.id,
    lane_id: lane.id,
    lane_name: lane.name,
    lane_kind: lane.kind || '',
    lane_color: lane.color ?? null,
    board_id: board.id,
    board_name: board.name,
    board_kind: board.kind || '',
    board_color: board.swatch ?? null,
    parent_board_id: board.parent_board_id ?? null,
    parent_board_name: board.parent_board_name ?? null,
    source: result.allocation.source || 'USER_MANUAL',
    confidence: result.allocation.confidence,
    card_status: result.card.status,
  }
}

/** Pure I→O — summary for linking onto an existing card from search. */
export function buildSummaryFromLinkResult(input: {
  result: LinkEmailToCardResult
  card: CardSearchResult
}): EmailAllocationSummary {
  const { result, card } = input
  return {
    allocation_id: result.allocation.id,
    card_id: result.card.id,
    lane_id: card.lane_id,
    lane_name: card.lane_name,
    lane_kind: '',
    lane_color: null,
    board_id: card.board_id,
    board_name: card.board_name,
    board_kind: '',
    board_color: null,
    source: result.allocation.source || 'USER_MANUAL',
    confidence: result.allocation.confidence,
    card_status: (result.card.status as EmailAllocationSummary['card_status'])
      || undefined,
  }
}
